import * as React from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {IStore} from '../../redux/interface';
import {ReactElement, useEffect, useState} from 'react';
import {requestForToken} from '../../utils/firebase/firebase';
import {addPushToken} from '../../redux/api/push';
import Layout from '../../components/layout';
import {RoutesUrl} from '../../utils/constants/routeConstants';
import Loader from '../../components/loader';
import {getUserData} from '../../redux/pageAsyncActions';
import {IUserProfile} from '@wholesalechange/chatcomponent';
import {isOnboardingPending} from '../../utils/helpers/onboardingHelpers';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  bareLayout?: boolean;
}

export const PrivateRoute: React.FunctionComponent<PrivateRouteProps> = (
  {
    children,
    requiredRole,
    bareLayout
  }
) => {
  const {
    auth0User, userProfile, settings
  } = useSelector((state: IStore) => state.auth);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const [token, setToken] = useState('');
  const location = useLocation();

  const addToken = async (permission: NotificationPermission) => {
    if (permission === 'granted') {
      const firebaseToken = await requestForToken();

      if (firebaseToken) {
        setToken(firebaseToken);
        if (auth0User) {
          addPushToken(firebaseToken).then(r => console.log(`Push token add result = ${r}`));
        }
      }
    }
  };

  useEffect(() => {
    const getToken = async () => {
      Notification.requestPermission()
        .then((permission) => addToken(permission))
        .catch((error) => {
          if (error instanceof TypeError) {
            Notification.requestPermission((permission) => {
              addToken(permission);
            });
          } else {
            throw error;
          }
        });
    };

    getToken();
  }, []);

  const isUserBlocked = () => {
    return auth0User && userProfile && userProfile.block;
  };

  const isUserAuthorised = () => {
    return auth0User;
  };

  const isUserProfileFilled = () => {
    if (userProfile && settings && settings.profileSettings
      && settings.profileSettings.blockUntilRequiredFieldsSet && settings.profileSettings.additionalFields) {
      const requiredFields = settings.profileSettings.additionalFields
        .filter(field => field.required)
        .map(field => field.name);

      return !requiredFields.some(field => !userProfile[field as keyof IUserProfile]);
    }

    return true;
  };

  const isUserInRole = () => {
    if (!requiredRole) {
      return true;
    }

    return userProfile?.roles?.includes(requiredRole);
  };

  if (!isUserAuthorised()) {
    return (
      <Navigate
        to={{pathname: RoutesUrl.Home}}
        replace
        state={{from: location}}
      />
    );
  }

  if (requiredRole && loading.includes(getUserData)) {
    return <Loader nested/>;
  }

  if (isUserBlocked() || !isUserInRole()) {
    return (
      <Navigate
        to={{pathname: RoutesUrl.AccessDenied}}
        replace
        state={{from: location}}
      />
    );
  }

  if (isOnboardingPending(settings, userProfile) && location.pathname !== RoutesUrl.Onboarding) {
    return (
      <Navigate
        to={{pathname: RoutesUrl.Onboarding}}
        replace
        state={{from: location}}
      />
    );
  }

  if (!isUserProfileFilled() && location.pathname !== RoutesUrl.Settings) {
    return (
      <Navigate
        to={{pathname: RoutesUrl.Settings}}
        replace
        state={{from: location}}
      />
    );
  }

  if (bareLayout) {
    return children as ReactElement;
  }

  return <Layout>{children as ReactElement}</Layout>;
};
